import React, { useState } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Field, SelectField } from "@/components/common/Field";
import { DatePicker } from "@/components/common/DatePicker";
import {
  DOC_STATUSES, APPROVAL_STATUSES, PAYMENT_STATUSES, COD_STATUSES, processingTone,
} from "@/lib/constants";
import { formatDate, formatINR } from "@/lib/format";
import {
  CheckCircle2, FileText, FileCheck2, IndianRupee, ScrollText, PartyPopper, Loader2, Lock,
} from "lucide-react";

function Stage({ icon: Icon, title, done, children }) {
  return (
    <div className="relative rounded-xl border border-border bg-card p-4 shadow-card">
      <div className="mb-3 flex items-center gap-2.5">
        <span className={cn("flex h-8 w-8 items-center justify-center rounded-lg", done ? "bg-success/10 text-success" : "bg-secondary text-secondary-foreground")}>
          <Icon className="h-4 w-4" />
        </span>
        <h3 className="text-sm font-bold text-foreground">{title}</h3>
        {done && <CheckCircle2 className="ml-auto h-5 w-5 text-success" />}
      </div>
      {children}
    </div>
  );
}

export const DealProcessingPanel = ({ lead, onUpdated }) => {
  const dp = lead.deal_processing || {};
  const [saving, setSaving] = useState(false);
  const [pay, setPay] = useState({
    payment_amount: dp.payment_amount ?? "",
    payment_date: dp.payment_date ?? "",
    payment_reference: dp.payment_reference ?? "",
    payment_remarks: dp.payment_remarks ?? "",
  });
  const [rejReason, setRejReason] = useState(dp.file_approval_rejection_reason ?? "");

  const patch = async (changes, msg) => {
    setSaving(true);
    try {
      const res = await api.patch(`/leads/${lead.id}/deal-processing`, changes);
      onUpdated?.(res.data);
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const codIssued = dp.cod_status === "Issued";
  const completed = dp.completed;

  return (
    <div className="space-y-3">
      {completed && (
        <div className="flex items-center gap-3 rounded-xl bg-success/10 p-4 ring-1 ring-inset ring-success/20">
          <PartyPopper className="h-6 w-6 text-success" />
          <div>
            <div className="text-sm font-bold text-success">Transaction Completed</div>
            <div className="text-xs text-success/80">Closed on {formatDate(dp.completed_date)}</div>
          </div>
        </div>
      )}

      {/* Deal done */}
      <Stage icon={CheckCircle2} title="Deal Done" done>
        <span className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-3 py-1 text-xs font-semibold text-success ring-1 ring-inset ring-success/20">
          <CheckCircle2 className="h-3.5 w-3.5" /> Done
        </span>
      </Stage>

      {/* Documents */}
      <Stage icon={FileText} title="Documents" done={dp.documents_status === "Received"}>
        <SelectField value={dp.documents_status || "Pending"}
          onChange={(v) => patch({ documents_status: v }, `Documents: ${v}`)} options={DOC_STATUSES} />
      </Stage>

      {/* File approval */}
      <Stage icon={FileCheck2} title="File Approval" done={dp.file_approval_status === "Approved"}>
        <SelectField value={dp.file_approval_status || "Pending"}
          onChange={(v) => patch({ file_approval_status: v }, `File approval: ${v}`)} options={APPROVAL_STATUSES} />
        {dp.file_approval_status === "Rejected" && (
          <div className="mt-3 space-y-2 rounded-lg bg-destructive/5 p-3">
            <Field label="Rejection reason">
              <Textarea rows={2} value={rejReason} onChange={(e) => setRejReason(e.target.value)} placeholder="Why was it rejected?" />
            </Field>
            <Button size="sm" variant="outline" disabled={saving}
              onClick={() => patch({ file_approval_rejection_reason: rejReason }, "Rejection reason saved")}>Save reason</Button>
          </div>
        )}
      </Stage>

      {/* Payment */}
      <Stage icon={IndianRupee} title="Payment" done={dp.payment_status === "Paid"}>
        <SelectField value={dp.payment_status || "Pending"}
          onChange={(v) => patch({ payment_status: v }, `Payment: ${v}`)} options={PAYMENT_STATUSES} />
        {dp.payment_status === "Paid" && (
          <div className="mt-3 space-y-3 rounded-lg bg-secondary/50 p-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Amount (₹)"><Input className="h-11" inputMode="numeric" value={pay.payment_amount}
                onChange={(e) => setPay({ ...pay, payment_amount: e.target.value })} placeholder="165000" /></Field>
              <Field label="Payment date"><DatePicker value={pay.payment_date} onChange={(v) => setPay({ ...pay, payment_date: v })} /></Field>
            </div>
            <Field label="Reference / UTR"><Input className="h-11" value={pay.payment_reference}
              onChange={(e) => setPay({ ...pay, payment_reference: e.target.value })} placeholder="UTR / transaction ref" /></Field>
            <Field label="Remarks"><Input className="h-11" value={pay.payment_remarks}
              onChange={(e) => setPay({ ...pay, payment_remarks: e.target.value })} placeholder="Optional" /></Field>
            <Button size="sm" variant="outline" disabled={saving}
              onClick={() => patch({
                payment_amount: pay.payment_amount === "" ? null : Number(pay.payment_amount),
                payment_date: pay.payment_date || null,
                payment_reference: pay.payment_reference || null,
                payment_remarks: pay.payment_remarks || null,
              }, "Payment details saved")}>Save payment details</Button>
          </div>
        )}
        {dp.payment_status === "Paid" && dp.payment_amount != null && (
          <p className="mt-2 text-xs text-muted-foreground">Recorded: <b className="tnum text-foreground">{formatINR(dp.payment_amount)}</b>{dp.payment_date ? ` on ${formatDate(dp.payment_date)}` : ""}</p>
        )}
      </Stage>

      {/* COD */}
      <Stage icon={ScrollText} title="COD" done={codIssued}>
        <SelectField value={dp.cod_status || "Pending"}
          onChange={(v) => patch({ cod_status: v }, `COD: ${v}`)} options={COD_STATUSES} />
      </Stage>

      {/* Complete */}
      {!completed ? (
        <Button variant="success" size="xl" className="w-full" disabled={!codIssued || saving}
          onClick={() => patch({ completed: true }, "Transaction marked Completed")}>
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : codIssued ? <PartyPopper className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
          {codIssued ? "Mark Transaction Completed" : "Issue COD to complete"}
        </Button>
      ) : (
        <Button variant="outline" size="lg" className="w-full" disabled={saving}
          onClick={() => patch({ completed: false }, "Reopened transaction")}>Reopen transaction</Button>
      )}
    </div>
  );
};
